import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Palette, Upload, Trash2, Save, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_LOGO_BYTES = 200 * 1024; // 200KB before base64 overhead
const PRESET_COLORS = ["#0047FF", "#FF5900", "#00D05A", "#8B00FF", "#DC2626", "#000000", "#0A0A0A", "#FFD700"];

/**
 * Club branding settings — logo (data URI) + brand accent color, used on the
 * cover of every PDF export (scenario + comparison bundle). Kept intentionally
 * small: base64 in Mongo, capped at 200KB. When users want a huge/production
 * logo they can trim in another tool first.
 */
export default function ClubBrandingPanel({ club, onUpdated }) {
  const t = useT();
  const [color, setColor] = useState(club?.brand_color || "#0047FF");
  const [logoDataUri, setLogoDataUri] = useState(club?.logo_data_uri || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [colorError, setColorError] = useState("");

  useEffect(() => {
    setColor(club?.brand_color || "#0047FF");
    setLogoDataUri(club?.logo_data_uri || "");
    setDirty(false);
    setColorError("");
  }, [club?.id, club?.brand_color, club?.logo_data_uri]);

  const validateColor = (v) => /^#[0-9A-Fa-f]{6}$/.test(v);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("Alleen afbeeldingen (PNG/JPEG/SVG) toegestaan."));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t("Logo is te groot (max 200KB). Verklein het bestand en probeer opnieuw."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = String(reader.result || "");
      // Verify the browser can actually decode it before persisting — otherwise
      // jsPDF will throw at export time and silently fall back to the color bar.
      const testImg = new Image();
      testImg.onload = () => { setLogoDataUri(dataUri); setDirty(true); };
      testImg.onerror = () => toast.error(t("Logo-bestand is beschadigd of onbekend formaat. Kies een andere PNG/JPEG."));
      testImg.src = dataUri;
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => { setLogoDataUri(""); setDirty(true); };

  const save = async () => {
    if (!validateColor(color)) {
      setColorError(t("Kleur moet formaat #RRGGBB hebben (bijv. #0047FF)."));
      toast.error(t("Kleur moet formaat #RRGGBB hebben (bijv. #0047FF)."));
      return;
    }
    setSaving(true);
    try {
      // Only send the fields the user actually changed so a save from the color
      // picker doesn't accidentally wipe a logo saved from another device.
      const body = {};
      if (color !== (club?.brand_color || "#0047FF")) body.brand_color = color;
      if (logoDataUri !== (club?.logo_data_uri || "")) body.logo_data_uri = logoDataUri;
      if (Object.keys(body).length === 0) { setSaving(false); return; }
      const { data } = await api.patch(`/clubs/${club.id}/branding`, body);
      toast.success(t("Huisstijl opgeslagen"));
      setDirty(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="club-branding-panel">
      <div className="stat-label inline-flex items-center gap-2">
        <Palette className="w-3 h-3" /> {t("Huisstijl voor PDF-exports")}
      </div>
      <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
        {t("Logo & merkkleur")}
      </h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        {t("Verschijnt op de cover van elke PDF-export (scenariorapport + vergelijkingsbundel). Houd het logo klein (< 200KB) voor snelle downloads.")}
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Logo section */}
        <div>
          <div className="stat-label mb-2">{t("Clublogo")}</div>
          <div
            className="border-2 border-dashed border-foreground p-4 min-h-[140px] flex items-center justify-center relative"
            style={{
              backgroundImage: "repeating-conic-gradient(hsl(var(--muted)) 0% 25%, transparent 0% 50%)",
              backgroundSize: "20px 20px",
            }}
            data-testid="club-branding-logo-preview"
          >
            {logoDataUri ? (
              <img src={logoDataUri} alt={t("Clublogo")} className="max-h-24 max-w-full object-contain" />
            ) : (
              <div className="text-muted-foreground text-sm text-center flex flex-col items-center gap-2">
                <ImageIcon className="w-6 h-6" />
                {t("Geen logo geüpload")}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <label className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> {logoDataUri ? t("Vervangen") : t("Uploaden")}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="club-branding-logo-file"
                className="hidden"
              />
            </label>
            {logoDataUri && (
              <button
                onClick={clearLogo}
                data-testid="club-branding-logo-clear"
                className="btn-brutal bg-background text-destructive px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 border-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" /> {t("Verwijderen")}
              </button>
            )}
          </div>
        </div>

        {/* Color section */}
        <div>
          <div className="stat-label mb-2">{t("Merkkleur")}</div>
          <div className="flex items-center gap-3 mb-3">
            <input
              type="color"
              value={color}
              onChange={(e) => { setColor(e.target.value.toUpperCase()); setDirty(true); }}
              data-testid="club-branding-color-picker"
              className="w-14 h-14 border-2 border-foreground cursor-pointer"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                const v = e.target.value.trim().toUpperCase();
                setColor(v);
                setDirty(true);
                setColorError(validateColor(v) ? "" : t("Kleur moet formaat #RRGGBB hebben (bijv. #0047FF)."));
              }}
              data-testid="club-branding-color-hex"
              className={`border-2 bg-background px-3 py-2 font-mono-num uppercase w-32 focus:outline-none focus:ring-2 focus:ring-primary ${colorError ? "border-destructive text-destructive" : "border-foreground"}`}
              placeholder="#0047FF"
            />
          </div>
          {colorError && (
            <div className="text-destructive text-xs -mt-1 mb-2" data-testid="club-branding-color-error">
              {colorError}
            </div>
          )}
          <div className="flex flex-wrap gap-2" data-testid="club-branding-color-presets">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setColor(c); setDirty(true); }}
                title={c}
                aria-label={c}
                data-testid={`club-branding-preset-${c.substring(1)}`}
                className={`w-8 h-8 border-2 ${color.toUpperCase() === c.toUpperCase() ? "border-foreground scale-110" : "border-foreground/30"}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="mt-4 border-2 border-foreground p-3 bg-background">
            <div className="stat-label mb-2">{t("Voorbeeld cover")}</div>
            <div className="flex items-center gap-3 bg-foreground text-background p-3">
              {logoDataUri ? (
                <img src={logoDataUri} alt="" className="w-8 h-8 object-contain bg-white" />
              ) : (
                <div className="w-3 h-8" style={{ backgroundColor: color }} />
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">MEERJARENPLAN ONLINE · SCENARIORAPPORT</div>
                <div className="font-display font-black uppercase tracking-tight text-lg">{club?.name || t("Clubnaam")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="club-branding-save"
          className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2 disabled:opacity-40"
        >
          <Save className="w-4 h-4" /> {saving ? t("Bezig…") : t("Huisstijl opslaan")}
        </button>
      </div>
    </div>
  );
}
